// src/oura/screens/ClientReport.tsx
//
// The client-facing AI Visibility Assessment — 8 print-ready A4 pages, built to
// the approved pitch mock (`oura-mock-report.html`).
//
// THIS IS THE ONLY REPORT. ourteam-backend#3802 records the decision: the
// frontend owns the template and the backend prints THIS PAGE headlessly with
// Playwright. There is deliberately no backend-side template — two templates
// drift within weeks and then the PDF a client receives stops matching what
// staff see on screen. So everything here has to survive being rendered by a
// browser with no session, no app chrome, and no JavaScript interaction.
//
// Consequences of that, which are why this file looks the way it does:
//
//   • Real CSS in a <style> block, not Tailwind. `@page`, millimetre units and
//     `page-break-after` have no Tailwind equivalent, and the print geometry is
//     the whole point of the document.
//   • No interactivity. Nothing to click, nothing that reveals on hover — a PDF
//     cannot hover, and anything hidden behind an interaction is content the
//     client never receives.
//   • A readiness marker (`data-report-ready`) so the renderer waits for real
//     data instead of a fixed timeout. Without it a slow assessment fetch
//     produces a confidently empty PDF, which is worse than a failed render.
//
// On honesty: every number is drawn from the assessment payload. Where a value
// was not measured, the block that would display it is OMITTED rather than
// zero-filled. A client-facing document is exactly where a fabricated 0 does
// the most damage, and several of these fields are legitimately null on a run
// that could not measure them.

import { useEffect } from "react";
import type { AeoAnswerCoverage, AeoAreaScore, AeoProbe, AeoSentiment } from "@service/aeo";
import type { AuditIssue, BusinessAudit } from "../audit";
import type { AiVisibility } from "../audit";
import { CLIENT_ISSUE_COPY } from "../components/site/siteCopy";
import { rivalsInstead } from "../lib/rivalsInstead";

const LOGO = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_wordmark.png`;

/** The 5-rung ladder copy, shared by the cover and the matrix. */
const LEVEL_NAMES = ["Getting started", "Foundations", "Building", "Established", "Authority"];

/**
 * The maturity matrix (page 6). Static editorial copy, not data — it describes
 * what each rung MEANS, which is the same for every business. Only which cell
 * is highlighted comes from the assessment.
 */
const MATRIX: Array<{ key: string; area: string; levels: [string, string, string, string, string] }> = [
    {
        key: "website_setup",
        area: "Website setup",
        levels: [
            "On-page basics",
            "Some page structure",
            "Consistent structure on a fast site",
            "Automated, well-structured site",
            "Cutting-edge AI standards",
        ],
    },
    {
        key: "content",
        area: "Your content",
        levels: [
            "Thin service pages",
            "Core services documented",
            "Pages that answer real questions",
            "Question-led content hierarchy",
            "Personalised, always-fresh",
        ],
    },
    {
        key: "reputation",
        area: "Reputation",
        levels: [
            "A few backlinks",
            "Active trust signals",
            "Proactive PR & thought leadership",
            "Recognised name, strong storytelling",
            "Widespread positive mentions",
        ],
    },
    {
        key: "clarity",
        area: "How clearly you explain",
        levels: [
            "Generic descriptions",
            "Clear service definitions",
            "Audience-specific pages",
            "Intent-matched explanations",
            "Quotable, answer-ready copy",
        ],
    },
];

/** Plain-English gloss per area, shown under its score bar. */
const AREA_BLURB: Record<string, string> = {
    website_setup: "The behind-the-scenes labels that let AI and Google read your pages.",
    content: "How much helpful detail your pages give, so AI has something worth quoting.",
    reputation: "Whether AI sees enough outside proof to recommend you.",
    clarity: "How plainly your pages say who you are and what you do.",
};

function fmtDate(iso: string | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function pct(value: number | null | undefined): string {
    if (value === null || value === undefined) return "—";
    return `${Math.round(value)}%`;
}

function probeQuestionKey(probe: AeoProbe): string {
    return (probe.query_id || probe.query || "").trim().toLowerCase();
}

/** Per question, whether ANY engine named the business. */
function namedByQuestion(probes: AeoProbe[] | undefined): Map<string, boolean> {
    const byQuestion = new Map<string, boolean>();
    for (const p of probes ?? []) {
        const key = probeQuestionKey(p);
        if (key) byQuestion.set(key, Boolean(byQuestion.get(key) || p.mentioned));
    }
    return byQuestion;
}

/**
 * Questions at least one engine named, out of the questions asked (#4207).
 * The backend's own count when the scan carries it; on an older scan, counted
 * from the answers the same way (a question once, whichever engine named it).
 * `null` when nothing was measured.
 */
function namedQuestions(
    visibility: AiVisibility | undefined, probes: AeoProbe[] | undefined, asked: number,
): { named: number; of: number } | null {
    const named = visibility?.questionsNamed;
    const of = visibility?.questionsOf ?? asked;
    if (typeof named === "number" && of > 0) return { named: Math.min(named, of), of };
    const byQuestion = namedByQuestion(probes);
    if (byQuestion.size === 0) return null;
    return { named: Array.from(byQuestion.values()).filter(Boolean).length, of: byQuestion.size };
}

const ENGINE_NAME: Record<string, string> = {
    "ChatGPT": "ChatGPT", "ChatGPT (app)": "ChatGPT", "OpenAI (API)": "ChatGPT",
    "Gemini": "Gemini", "Google AI": "Google's AI",
};

/** The engines this scan actually measured, in the order the scan lists them:
 *  ChatGPT, Gemini, Google's AI. Every report read "ChatGPT and Gemini",
 *  Google's AI measured or not (#4207). */
function measuredEngines(visibility: AiVisibility | undefined): string[] {
    const names: string[] = [];
    for (const e of visibility?.engines ?? []) {
        const measured = e.probed !== false && (e.answered ?? e.coverage?.answered ?? 1) > 0;
        const name = ENGINE_NAME[e.engine] ?? e.engine;
        if (measured && !names.includes(name)) names.push(name);
    }
    return names.length > 0 ? names : ["ChatGPT", "Gemini"];
}

/** "A", "A and B", "A, B and C" (or "or"). */
function joinNames(names: string[], word: "and" | "or"): string {
    return names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} ${word} ${names[names.length - 1]}`;
}

export interface ClientReportData {
    businessName: string;
    domain: string;
    city: string;
    capturedAt: string;
    audit?: BusinessAudit;
    visibility?: AiVisibility;
    probes?: AeoProbe[];
    areaScores?: AeoAreaScore[];
    maturityLevel?: number | null;
    maturityLabel?: string | null;
    aiReadability?: number | null;
    sentiment?: AeoSentiment | null;
    answerCoverage?: AeoAnswerCoverage | null;
    questionsTested?: number;
}

/** Running header + footer, repeated on every page. */
function Furniture({ domain, foot = false }: { domain: string; foot?: boolean }) {
    if (foot) {
        return (
            <div className="rf">
                <span className="logo"><img className="logo-img foot" src={LOGO} alt="ourteam" /></span>
                <span className="rf-conf">Confidential</span>
            </div>
        );
    }
    return (
        <div className="rh">
            <span className="logo"><img className="logo-img" src={LOGO} alt="ourteam" /></span>
            <span className="rh-meta">AI Visibility Assessment · {domain}</span>
        </div>
    );
}

export function ClientReport({ data }: { data: ClientReportData }) {
    const {
        businessName, domain, city, capturedAt,
        audit, visibility, probes, areaScores,
        maturityLevel, maturityLabel, aiReadability, sentiment, answerCoverage,
    } = data;

    const questionsTested = data.questionsTested ?? 0;
    // How often AI names the business: the app's own number, questions named by
    // at least one engine. `mentions` is NOT a rate — it adds the engines'
    // answers together, which printed "named in 106% of answers" (#4207).
    const named = namedQuestions(visibility, probes, questionsTested);
    const namedPct = named ? (named.named / named.of) * 100 : null;
    // The share of questions whose answer cited the client's own site, as the
    // backend divides it. `citations` is a count of answers, not a rate either.
    const share = visibility?.citationShare;
    const citedPct = share && !share.absent && share.denominator > 0 ? share.share * 100 : null;
    const engineNames = measuredEngines(visibility);

    /**
     * The captured answer on page 2 — a REAL probe where the business was not
     * mentioned but rivals were. That specific combination is the entire
     * argument of the page: customers asked, got names, and none were theirs.
     * Without one, the page is dropped rather than filled with an example.
     *
     * From a question NO engine named, the same population as the count beside
     * it: an engine's miss on a question another engine named is not a customer
     * who never heard the name (#4207).
     */
    // Questions no engine named. Counting unnamed probe ROWS gave "27 of the 50
    // questions" on a scan where 48 of 50 were named: there is a row per engine.
    const missedCount = named ? named.of - named.named : 0;
    const byQuestion = namedByQuestion(probes);
    const missedProbe = missedCount > 0
        ? (probes ?? []).find((p) => byQuestion.get(probeQuestionKey(p)) === false && (p.rivals?.length ?? 0) > 0)
        : undefined;

    const level = maturityLevel ?? null;
    const levelName = maturityLabel ?? (level ? LEVEL_NAMES[level - 1] : "");

    // Which matrix cell to highlight per area, from each area's own 1-5 band.
    const areaLevel = new Map<string, number>();
    for (const area of areaScores ?? []) {
        if (area.level_1_to_5) areaLevel.set(area.key, area.level_1_to_5);
    }

    // Issues worth naming to a client, worst first. Anything without client-
    // readable copy is skipped — a raw check key in a client PDF is noise.
    const issues: AuditIssue[] = (audit?.issues ?? [])
        .filter((i) => CLIENT_ISSUE_COPY[i.key] && i.count > 0)
        .sort((a, b) => {
            const rank = { error: 0, warning: 1, notice: 2 } as const;
            return rank[a.severity] - rank[b.severity] || b.count - a.count;
        })
        .slice(0, 4);

    const thinPages = (audit?.issues ?? []).find((i) => i.key === "thin_content")?.pages ?? [];
    // Only a rival named where the business was NOT may be said to take an answer
    // (#4237). `competitorWins` also lists rivals on questions the business is named
    // on, so it cannot decide who is listed or what they are listed for.
    const insteadRivals = rivalsInstead(visibility?.competitorWins);
    const competitors = insteadRivals.rivals;

    // Signals to the renderer that every number is on the page. Playwright waits
    // for this instead of a fixed delay, so a slow fetch cannot yield a PDF with
    // half the document empty.
    useEffect(() => {
        document.body.setAttribute("data-report-ready", "1");
        return () => { document.body.removeAttribute("data-report-ready"); };
    }, []);

    return (
        // Every rule in CSS below is scoped under `.oura-report` (it keeps the
        // template's print styles from leaking into the app shell). A bare
        // fragment left that class on no element, so the whole stylesheet was
        // dead and the PDF printed unstyled (#4118).
        <div className="oura-report">
            <style>{CSS}</style>

            {/* ══ 1 · COVER ══ */}
            <div className="page">
                <Furniture domain={domain} />
                <div className="stack">
                    <div className="hero">
                        <span className="hero-tag">Answer Engine Optimisation</span>
                        {/* Said only when it is true: at 96% named it read "AI recommends your
                            competitors, not you." (#4134). */}
                        {/* Both rival claims are said only when a rival really took an answer
                            (#4237): when the scan measured it and none did, the page that
                            follows says so, and a headline saying otherwise contradicts it. */}
                        <h1>
                            {insteadRivals.none && namedPct !== null
                                ? namedPct > 50
                                    ? <>AI names you<br />in most answers.</>
                                    : <>AI does not name you<br />in most answers yet.</>
                                : namedPct !== null && namedPct >= 50
                                    ? <>AI names you.<br />Rivals still take some answers.</>
                                    : <>AI recommends your<br />competitors, not you.</>}
                        </h1>
                        <p>
                            When customers ask {joinNames(engineNames, "or")} for a {audit ? "business" : "provider"} like
                            yours{city ? ` in ${city}` : ""}, {businessName} is named in{" "}
                            {namedPct !== null ? `${pct(namedPct)} of questions` : "very few answers"}.
                            {/* competitorWins are the ones that win SOME answers, not ones named more often (#4134) */}
                            {competitors.length > 0 &&
                                ` ${competitors.length} competitor${competitors.length === 1 ? " is" : "s are"} named in answers that leave you out.`}
                        </p>
                        {level !== null && (
                            <div className="hero-score">
                                <div>
                                    <div className="hero-score-l">Overall AI-readiness</div>
                                    <div className="ladder">
                                        {[1, 2, 3, 4, 5].map((r) => (
                                            <i key={r} className={r <= level ? "rung on" : "rung"} />
                                        ))}
                                    </div>
                                    <div style={{ fontSize: "10.5pt" }}>{levelName}</div>
                                </div>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                                    <span className="hero-score-n">{level}</span>
                                    <span className="hero-score-d">/ 5</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="meta-row">
                        <div><div className="meta-k">Prepared for</div><div className="meta-v">{businessName}</div></div>
                        {city && <div><div className="meta-k">Market</div><div className="meta-v">{city}</div></div>}
                        <div><div className="meta-k">Date</div><div className="meta-v">{fmtDate(capturedAt)}</div></div>
                    </div>

                    <div className="note">
                        We asked {joinNames(engineNames, "and")} the{" "}
                        <strong>{questionsTested || "50"} questions</strong> your customers actually ask
                        before choosing, recorded every answer, and checked how easily AI can read your
                        website. Everything that follows is in plain language, with the fastest ways to
                        improve.
                    </div>
                </div>
                <Furniture domain={domain} foot />
            </div>

            {/* ══ 2 · THE MOMENT ══ — only with a real captured answer */}
            {missedProbe && (
                <div className="page">
                    <Furniture domain={domain} />
                    <span className="eyebrow-pill">What your customer sees</span>
                    <div className="stack">
                        <h2>We asked {missedProbe.engine === "gemini" ? "Gemini" : "ChatGPT"} what your<br />customers ask it.</h2>
                        <p className="sub">This is a real answer, captured during the scan. Your business is not in it.</p>

                        <div className="quote">
                            <div className="quote-h">
                                Asked <strong>{fmtDate(capturedAt)}</strong> ·{" "}
                                {missedProbe.engine === "gemini" ? "Gemini" : "ChatGPT"} ·{" "}
                                &ldquo;{missedProbe.query}&rdquo;
                            </div>
                            <div className="quote-b">
                                <Highlighted text={missedProbe.answer} terms={missedProbe.rivals} />
                            </div>
                            <div className="quote-f">{businessName} was not mentioned in this answer.</div>
                        </div>

                        {missedCount > 0 && questionsTested > 0 && (
                            <div className="note">
                                This happened in <strong>{missedCount} of the {questionsTested} questions</strong> we
                                tested. Every one of those is a customer who asked, got a recommendation, and never
                                heard your name.
                            </div>
                        )}

                        <div className="stack-s" style={{ marginTop: "2mm" }}>
                            <h3>Why it matters now</h3>
                            <p className="sub">
                                People increasingly ask an assistant instead of scrolling a results page. When they
                                do, they usually act on the first two or three names they are given. Being absent
                                from the answer is closer to being closed than to ranking low.
                            </p>
                        </div>
                    </div>
                    <Furniture domain={domain} foot />
                </div>
            )}

            {/* ══ 3 · THE NUMBERS ══ */}
            <div className="page">
                <Furniture domain={domain} />
                <span className="eyebrow-pill">AI visibility snapshot</span>
                <div className="stack">
                    <h2>How often AI recommends you today</h2>

                    <div className="tiles">
                        <Tile value={pct(namedPct)} label="How often AI names you" />
                        <Tile value={pct(citedPct)} label="How often AI links to you" />
                        {typeof visibility?.score === "number" && (
                            <Tile value={`${visibility.score}`} label="Your AI visibility score" />
                        )}
                        {/* Omitted, not zeroed, when the run could not measure them. */}
                        {sentiment && (
                            <Tile value={`${sentiment.score}%`} label="How positively AI describes you" />
                        )}
                        {aiReadability !== null && aiReadability !== undefined && (
                            <Tile value={`${aiReadability.toFixed(1)}/10`} label="How easily AI reads your site" />
                        )}
                        {questionsTested > 0 && (
                            <Tile value={`${questionsTested}`} label="Questions we tested" />
                        )}
                    </div>

                    {sentiment && aiReadability !== null && aiReadability !== undefined && (
                        <div className="stack-s" style={{ marginTop: "2mm" }}>
                            <h3>What the two strongest numbers mean</h3>
                            <p className="sub">
                                A {sentiment.score}% positive tone means the reputation work is largely done — AI is
                                not saying anything bad about you. A {aiReadability.toFixed(1)}/10 readability score
                                means the machine cannot easily understand your pages. Together they point at one
                                conclusion: this is a visibility problem, not a reputation problem, and visibility is
                                the faster of the two to fix.
                            </p>
                        </div>
                    )}
                </div>
                <Furniture domain={domain} foot />
            </div>

            {/* ══ 4 · COMPETITORS ══ */}
            {(competitors.length > 0 || insteadRivals.none) && (
                <div className="page">
                    <Furniture domain={domain} />
                    <span className="eyebrow-pill">Competitive position</span>
                    <div className="stack">
                        <h2>Who AI recommends instead</h2>
                        {competitors.length === 0 ? (
                            // Measured, and nobody took an answer: said so, rather than a list of rivals that never beat you (#4237).
                            <p className="sub">
                                In the questions we tested, no other business was named in your place.
                            </p>
                        ) : (
                            <>
                                <p className="sub">
                                    The businesses named in answers that leave you out.
                                </p>

                                {competitors.slice(0, 3).map((c) => (
                                    <div className="comp" key={c.name}>
                                        <div className="comp-n">{c.name}</div>
                                        <div className="comp-w">
                                            <b>Wins on:</b> {c.queries.slice(0, 2).join("; ")}
                                        </div>
                                        <div className="comp-x">
                                            Named in your place on {c.takes} of the questions we tested.
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                    <Furniture domain={domain} foot />
                </div>
            )}

            {/* ══ 5 · WHY ══ */}
            {audit && (
                <div className="page">
                    <Furniture domain={domain} />
                    <span className="eyebrow-pill">Why it is happening</span>
                    <div className="stack">
                        <h2>What is stopping AI from<br />reading your site</h2>

                        <div className="tiles" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
                            <Tile value={`${audit.pagesCrawled}`} label="Pages crawled" />
                            <Tile value={`${audit.errors}`} label="Errors found" tone="var(--rose)" />
                            <Tile value={`${audit.warnings}`} label="Warnings found" tone="var(--amber)" />
                        </div>

                        {issues.length > 0 && (
                            <div className="stack-s">
                                <h3>The findings that matter most</h3>
                                <div>
                                    {issues.map((i) => (
                                        <div className="iss" key={i.key}>
                                            <div className="iss-l">
                                                {CLIENT_ISSUE_COPY[i.key].label}
                                                <span>{CLIENT_ISSUE_COPY[i.key].why}</span>
                                            </div>
                                            <div className={`iss-c sev-${i.severity === "error" ? "e" : i.severity === "warning" ? "w" : "n"}`}>
                                                {i.count} {i.key === "missing_alt" ? "images" : "pages"}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {thinPages.length > 0 && (
                            <div className="stack-s" style={{ marginTop: "1mm" }}>
                                <h3>Pages to strengthen first</h3>
                                <div>
                                    {thinPages.slice(0, 3).map((p) => (
                                        <div className="pg" key={p.url}>
                                            <div className="pg-u">{p.url}</div>
                                            <div className="pg-m">{p.meta} &middot; aim for 300+</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {answerCoverage && (
                            <div className="note">
                                Your site currently answers about{" "}
                                <strong>{answerCoverage.coveragePct}%</strong> of the{" "}
                                {answerCoverage.total} questions customers ask. Each missing answer is one an AI
                                assistant resolves using a competitor&rsquo;s page instead of yours.
                            </div>
                        )}
                    </div>
                    <Furniture domain={domain} foot />
                </div>
            )}

            {/* ══ 6 · WHERE YOU SIT ══ */}
            {!!areaScores?.length && (
                <div className="page">
                    <Furniture domain={domain} />
                    <span className="eyebrow-pill">Where you sit</span>
                    <div className="stack">
                        <h2>Your position, and the next rung</h2>
                        <p className="sub">
                            Four areas decide whether AI recommends a business. Highlighted cells are where you
                            are today.
                        </p>

                        <table className="mx">
                            <thead>
                                <tr>
                                    <th style={{ width: "22%" }}>Area</th>
                                    {["Foundations", "Structure", "Consistency", "Pillar", "Authority"].map((n, i) => (
                                        <th key={n}>Level {i + 1}<br />{n}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {MATRIX.map((row) => (
                                    <tr key={row.key}>
                                        <td>{row.area}</td>
                                        {row.levels.map((cell, i) => (
                                            <td key={cell} className={areaLevel.get(row.key) === i + 1 ? "here" : undefined}>
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="stack-s" style={{ marginTop: "2mm" }}>
                            <h3>How each area scored</h3>
                            <div>
                                {/* Weakest first — the client should read the biggest gap at the
                                    top, not hunt for it. Unmeasured areas sort last and say so. */}
                                {[...areaScores]
                                    .sort((a, b) => (a.score_10 ?? 99) - (b.score_10 ?? 99))
                                    .map((area) => (
                                        <div className="area" key={area.key}>
                                            <div className="area-e">{area.label}</div>
                                            <div className="area-r">
                                                <div className="area-t">{AREA_BLURB[area.key] ?? ""}</div>
                                                <div className="area-s">
                                                    <div className="bar">
                                                        <div
                                                            className="bar-f"
                                                            style={{ width: `${(area.score_10 ?? 0) * 10}%` }}
                                                        />
                                                    </div>
                                                    <div className="area-v">
                                                        {area.measured && area.score_10 !== null
                                                            ? `${area.score_10.toFixed(1)}/10`
                                                            : "—"}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                    <Furniture domain={domain} foot />
                </div>
            )}

            {/* ══ 7 · PRIORITIES ══ */}
            {!!audit?.recommendations?.length && (
                <div className="page">
                    <Furniture domain={domain} />
                    <span className="eyebrow-pill">Where to start</span>
                    <div className="stack">
                        <h2>Your top 3 priorities</h2>
                        <p className="sub">For the fastest gain in how often AI names and recommends you.</p>

                        {audit.recommendations
                            .filter((r) => r.priority === "high")
                            .concat(audit.recommendations.filter((r) => r.priority !== "high"))
                            .slice(0, 3)
                            .map((r, i) => (
                                <div className="prio" key={r.id}>
                                    <div className="prio-n">{i + 1}</div>
                                    <div>
                                        <h3>{r.title}</h3>
                                        <div className="prio-b">{r.impact}</div>
                                    </div>
                                </div>
                            ))}

                        <div className="note">
                            <strong>The bottom line.</strong> {businessName} is well regarded and
                            under-surfaced. Fuller pages on the priorities above, complete page labels, and a
                            few trusted outside mentions are what move you up.
                        </div>
                    </div>
                    <Furniture domain={domain} foot />
                </div>
            )}

            {/* ══ 8 · PROOF ══ */}
            <div className="page">
                <Furniture domain={domain} />
                <span className="eyebrow-pill">Proof</span>
                <div className="stack">
                    <h2>Check any of this yourself</h2>
                    <p className="sub">
                        Every finding here can be reproduced in about a minute using free public tools.
                        Answers shift between sessions and devices, so results may vary slightly from the
                        date captured.
                    </p>

                    <div className="ex">
                        <div className="ex-h">
                            <span className="ex-lab">EXHIBIT A</span>
                            <span className="ex-tag">Proves: no structured data</span>
                        </div>
                        <div className="ex-b">
                            <div className="ex-t">Google&rsquo;s Rich Results Test finds no labels on your homepage</div>
                            <div className="ex-shot">
                                Screenshot — Rich Results Test<br />search.google.com/test/rich-results
                            </div>
                            <div className="ex-x">
                                <b>What to look at:</b> the result reads &ldquo;No items detected&rdquo;.<br />
                                <b>Check it yourself:</b> paste {domain} into search.google.com/test/rich-results
                            </div>
                        </div>
                    </div>

                    {missedProbe && (
                        <div className="ex">
                            <div className="ex-h">
                                <span className="ex-lab">EXHIBIT B</span>
                                <span className="ex-tag">Proves: AI names rivals, not you</span>
                            </div>
                            <div className="ex-b">
                                <div className="ex-t">
                                    {missedProbe.engine === "gemini" ? "Gemini" : "ChatGPT"} recommends competitors
                                    for a customer question
                                </div>
                                <div className="ex-shot">
                                    Screenshot — {missedProbe.engine === "gemini" ? "Gemini" : "ChatGPT"} answer<br />
                                    captured {fmtDate(capturedAt)}
                                </div>
                                <div className="ex-x">
                                    <b>What to look at:</b> {missedProbe.rivals.slice(0, 3).join(", ")} named;{" "}
                                    {businessName} absent.<br />
                                    <b>Check it yourself:</b> ask{" "}
                                    {missedProbe.engine === "gemini" ? "Gemini" : "ChatGPT"} &ldquo;
                                    {missedProbe.query}&rdquo;
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <Furniture domain={domain} foot />
            </div>
        </div>
    );
}

function Tile({ value, label, tone }: { value: string; label: string; tone?: string }) {
    return (
        <div className="tile">
            <div className="tile-n" style={tone ? { color: tone } : undefined}>{value}</div>
            <div className="tile-l">{label}</div>
        </div>
    );
}

/**
 * The quoted AI answer with rival names marked.
 *
 * Marking is what makes the page land — the client sees, in the assistant's own
 * words, three competitors highlighted and themselves missing. Names are escaped
 * into the regex because a business name can legitimately contain regex
 * metacharacters (&, +, parentheses), and one such name would otherwise throw
 * while building the page.
 */
function Highlighted({ text, terms }: { text: string; terms: string[] }) {
    const clean = (terms ?? []).map((t) => t.trim()).filter(Boolean);
    if (clean.length === 0) return <>{text}</>;

    const escaped = clean.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const parts = text.split(new RegExp(`(${escaped.join("|")})`, "gi"));
    const lower = new Set(clean.map((t) => t.toLowerCase()));

    return (
        <>
            {parts.map((part, i) =>
                lower.has(part.toLowerCase())
                    ? <span className="hit" key={i}>{part}</span>
                    : <span key={i}>{part}</span>,
            )}
        </>
    );
}

// Print geometry and the document's visual identity, ported from the approved
// mock. Kept as one string so the whole template travels together — the BE
// renders this page, so a stylesheet split across files is a stylesheet that can
// arrive incomplete.
const CSS = `
@page { size: A4; margin: 0; }

.oura-report {
  --blue:#0B49FE; --blue-dark:#0838D0; --blue-deep:#0A2FA3;
  --blue-50:#EAF0FF; --blue-100:#E1E9FE;
  --ink:#0F172A; --ink-2:#475569; --ink-3:#94A3B8;
  --rule:#E2E8F0; --rule-2:#CBD5E1; --paper:#FFFFFF;
  --emerald:#059669; --amber:#D97706; --rose:#E11D48;
  background:#6B7280; color:var(--ink);
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  font-size:10.5pt; line-height:1.55; -webkit-font-smoothing:antialiased;
}

.oura-report .page {
  width:210mm; min-height:297mm; padding:16mm 18mm 14mm;
  margin:0 auto 10px; background:var(--paper); box-sizing:border-box;
  display:flex; flex-direction:column; position:relative;
}
@media print {
  .oura-report { background:#fff; }
  .oura-report .page { margin:0; box-shadow:none; page-break-after:always; }
  .oura-report .page:last-child { page-break-after:auto; }
  .no-print { display:none !important; }
}

.oura-report .rh { display:flex; align-items:center; justify-content:space-between; gap:1rem; padding-bottom:9mm; }
.oura-report .rh-meta { font-size:7.2pt; letter-spacing:.09em; text-transform:uppercase; color:var(--ink-3); font-weight:600; text-align:right; }
.oura-report .rf { margin-top:auto; padding-top:8mm; display:flex; align-items:flex-end; justify-content:space-between; }
.oura-report .rf-conf { font-size:7.2pt; color:var(--ink-3); }
.oura-report .logo { display:inline-flex; align-items:center; }
.oura-report .logo-img { height:15px; width:auto; display:block; }
.oura-report .logo-img.foot { height:13px; opacity:.95; }

.oura-report h1 { font-size:25pt; line-height:1.12; letter-spacing:-.025em; font-weight:700; margin:0; }
.oura-report h2 { font-size:19pt; line-height:1.15; letter-spacing:-.022em; font-weight:700; margin:0; }
.oura-report h3 { font-size:11.5pt; line-height:1.3; font-weight:700; margin:0; }
.oura-report p { margin:0; }
.oura-report .sub { font-size:10.5pt; color:var(--ink-2); line-height:1.6; }

.oura-report .eyebrow-pill {
  display:block; background:var(--blue-50); color:var(--blue-dark);
  font-size:7.4pt; font-weight:700; letter-spacing:.12em; text-transform:uppercase;
  padding:5px 12px; border-radius:999px; margin-bottom:7mm;
}
.oura-report .stack { display:flex; flex-direction:column; gap:4mm; }
.oura-report .stack-s { display:flex; flex-direction:column; gap:2.5mm; }

.oura-report .hero { background:linear-gradient(135deg,var(--blue-dark) 0%,var(--blue-deep) 100%); border-radius:10px; padding:11mm 10mm; color:#fff; }
.oura-report .hero-tag { display:inline-block; background:rgba(255,255,255,.18); font-size:7.4pt; font-weight:700; letter-spacing:.12em; text-transform:uppercase; padding:5px 12px; border-radius:999px; margin-bottom:6mm; }
.oura-report .hero h1 { color:#fff; margin-bottom:3mm; }
.oura-report .hero p { color:rgba(255,255,255,.88); font-size:10.5pt; line-height:1.55; }
.oura-report .hero-score { margin-top:8mm; background:rgba(255,255,255,.13); border-radius:8px; padding:6mm 7mm; display:flex; align-items:center; justify-content:space-between; gap:6mm; }
.oura-report .hero-score-l { font-size:7.4pt; letter-spacing:.12em; text-transform:uppercase; color:rgba(255,255,255,.75); font-weight:600; }
.oura-report .ladder { display:flex; gap:4px; margin:3.5mm 0 2mm; }
.oura-report .rung { width:30px; height:5px; border-radius:999px; background:rgba(255,255,255,.28); }
.oura-report .rung.on { background:#fff; }
.oura-report .hero-score-n { font-size:30pt; font-weight:700; line-height:1; color:#fff; }
.oura-report .hero-score-d { font-size:12pt; color:rgba(255,255,255,.7); }

.oura-report .meta-row { display:flex; gap:14mm; }
.oura-report .meta-k { font-size:9pt; font-weight:700; }
.oura-report .meta-v { font-size:9.5pt; color:var(--ink-2); }

.oura-report .note { border-left:3px solid var(--blue); background:var(--blue-50); padding:5mm 6mm; border-radius:0 6px 6px 0; font-size:9.5pt; line-height:1.6; color:var(--ink-2); }
.oura-report .note strong { color:var(--ink); }

.oura-report .prio { display:grid; grid-template-columns:auto 1fr; gap:5mm; align-items:start; }
.oura-report .prio-n { width:22px; height:22px; border-radius:5px; background:var(--blue); color:#fff; font-size:9.5pt; font-weight:700; display:flex; align-items:center; justify-content:center; flex:none; margin-top:1px; }
.oura-report .prio-b { font-size:9.5pt; color:var(--ink-2); line-height:1.6; }

.oura-report .tiles { display:grid; grid-template-columns:repeat(3,1fr); gap:3.5mm; }
.oura-report .tile { background:#F8FAFC; border-radius:7px; padding:5mm 5mm 5.5mm; }
.oura-report .tile-n { font-size:17pt; font-weight:700; color:var(--blue); line-height:1.1; }
.oura-report .tile-l { font-size:8.2pt; color:var(--ink-2); margin-top:1.5mm; line-height:1.4; }

.oura-report .quote { border:1px solid var(--rule); border-radius:8px; overflow:hidden; }
.oura-report .quote-h { background:#F8FAFC; border-bottom:1px solid var(--rule); padding:3.5mm 5mm; font-size:8.2pt; color:var(--ink-2); }
.oura-report .quote-h strong { color:var(--ink); }
.oura-report .quote-b { padding:5mm; font-size:9.5pt; line-height:1.65; color:var(--ink); }
.oura-report .quote-b .hit { background:#FEF2F2; color:#9F1239; padding:0 3px; border-radius:3px; font-weight:600; }
.oura-report .quote-f { border-top:1px solid var(--rule); padding:3mm 5mm; font-size:8pt; color:var(--ink-3); }

.oura-report .comp { border:1px solid var(--rule); border-radius:8px; padding:5mm; }
.oura-report .comp-n { font-size:10.5pt; font-weight:700; }
.oura-report .comp-d { font-size:8.8pt; color:var(--blue); }
.oura-report .comp-w { font-size:9pt; margin-top:2.5mm; }
.oura-report .comp-w b { font-weight:700; }
.oura-report .comp-x { font-size:9pt; color:var(--ink-2); margin-top:2mm; line-height:1.55; }

.oura-report .area { padding:4.5mm 0; border-bottom:1px solid var(--rule); }
.oura-report .area:last-child { border-bottom:0; }
.oura-report .area-e { font-size:7.4pt; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--blue-dark); }
.oura-report .area-r { display:flex; align-items:flex-start; justify-content:space-between; gap:8mm; margin-top:1.5mm; }
.oura-report .area-t { font-size:11pt; font-weight:700; line-height:1.35; flex:1; }
.oura-report .area-s { display:flex; align-items:center; gap:4mm; flex:none; }
.oura-report .bar { width:42mm; height:6px; border-radius:999px; background:var(--rule); overflow:hidden; }
.oura-report .bar-f { height:100%; border-radius:999px; background:var(--blue); }
.oura-report .area-v { font-size:11pt; font-weight:700; color:var(--blue); width:17mm; text-align:right; }

.oura-report table.mx { width:100%; border-collapse:collapse; font-size:7.8pt; }
.oura-report table.mx th { background:var(--blue); color:#fff; text-align:left; padding:3.5mm 3mm; font-weight:700; line-height:1.3; }
.oura-report table.mx th:first-child { border-radius:6px 0 0 0; }
.oura-report table.mx th:last-child { border-radius:0 6px 0 0; }
.oura-report table.mx td { padding:3.5mm 3mm; border-bottom:1px solid var(--rule); color:var(--ink-2); line-height:1.35; }
.oura-report table.mx td:first-child { font-weight:700; color:var(--ink); }
.oura-report table.mx td.here { background:var(--blue-50); color:var(--blue-dark); font-weight:700; box-shadow:inset 0 0 0 1.5px var(--blue); }

.oura-report .pg { padding:3.5mm 0; border-bottom:1px solid var(--rule); }
.oura-report .pg:last-child { border-bottom:0; }
.oura-report .pg-u { font-size:9.2pt; color:var(--blue); font-weight:600; word-break:break-all; }
.oura-report .pg-m { font-size:8.5pt; color:var(--ink-2); margin-top:1mm; }

.oura-report .iss { display:flex; align-items:baseline; justify-content:space-between; gap:5mm; padding:3mm 0; border-bottom:1px solid var(--rule); }
.oura-report .iss:last-child { border-bottom:0; }
.oura-report .iss-l { font-size:9.5pt; }
.oura-report .iss-l span { display:block; font-size:8.3pt; color:var(--ink-2); margin-top:.5mm; }
.oura-report .iss-c { font-size:9.5pt; font-weight:700; flex:none; }
.oura-report .sev-e { color:var(--rose); }
.oura-report .sev-w { color:var(--amber); }
.oura-report .sev-n { color:var(--ink-3); }

.oura-report .ex { border:1px solid var(--rule); border-radius:8px; overflow:hidden; }
.oura-report .ex-h { background:#F8FAFC; border-bottom:1px solid var(--rule); padding:3.5mm 5mm; display:flex; align-items:center; gap:4mm; }
.oura-report .ex-lab { font-size:7.4pt; font-weight:700; letter-spacing:.1em; color:var(--ink-2); }
.oura-report .ex-tag { font-size:7.4pt; font-weight:700; color:#9F1239; background:#FEF2F2; padding:3px 8px; border-radius:999px; }
.oura-report .ex-b { padding:5mm; }
.oura-report .ex-t { font-size:10pt; font-weight:700; margin-bottom:2.5mm; }
.oura-report .ex-shot { border:1px solid var(--rule-2); border-radius:5px; background:#F1F5F9; height:34mm; display:flex; align-items:center; justify-content:center; font-size:8.2pt; color:var(--ink-3); text-align:center; padding:4mm; }
.oura-report .ex-x { font-size:8.8pt; color:var(--ink-2); margin-top:3mm; line-height:1.55; }
.oura-report .ex-x b { color:var(--ink); }
`;
